import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Icon, Input, Spinner, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./accept-invite.css";

/**
 * Completing a "forgot password" link.
 *
 * Deliberately does NOT sign the person in afterwards, unlike accepting an
 * invitation: a password reset is exactly the moment an account may have been
 * compromised, and the server has already revoked every session as part of
 * completing it. Landing back on the sign-in form, now with the new password,
 * is the honest state to show — not a session that quietly survived the reset.
 */

const MIN_PASSWORD = 12;

export function ResetPassword() {
  usePageTitle("Reset password");
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [done, setDone] = useState(false);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/auth/password-reset/${encodeURIComponent(token)}`)
      .catch(() => !cancelled && setInvalid(true))
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const canSubmit = password.length >= MIN_PASSWORD && password === confirm && !submitting;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitting(true);
    api
      .post("/auth/reset-password", { token, password })
      .then(() => setDone(true))
      .catch((error: unknown) => {
        // A token can expire in the gap between the page loading and the form
        // being submitted — treated the same as failing the initial check.
        if (error instanceof ApiError && error.code === "INVALID_RESET") {
          setInvalid(true);
          return;
        }
        toast(error instanceof ApiError ? error.message : "Could not reset that password.", { tone: "danger" });
      })
      .finally(() => setSubmitting(false));
  }, [canSubmit, token, password, toast]);

  if (checking) {
    return (
      <div className="accept">
        <div className="accept__card accept__card--centred">
          <Spinner />
          <p>Checking that link…</p>
        </div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="accept">
        <div className="accept__card">
          <Brand />
          <h1>This link is no longer valid.</h1>
          <p className="accept__muted">
            It may have expired or already been used. Reset links last one hour — request a new one if you still
            need to change your password.
          </p>
          <Button variant="secondary" onClick={() => navigate("/forgot-password")}>
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="accept">
        <div className="accept__card">
          <Brand />
          <h1>Your password has been changed.</h1>
          <p className="accept__muted">
            For your security, every other device has been signed out. Sign in again with your new password.
          </p>
          <Button onClick={() => navigate("/login")}>Go to sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept">
      <form
        className="accept__card"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Brand />

        <h1>Choose a new password</h1>

        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={`At least ${MIN_PASSWORD} characters. A memorable phrase beats a short, complicated one.`}
          error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
          autoFocus
          required
        />

        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? "These do not match." : undefined}
          required
        />

        <Button type="submit" fullWidth loading={submitting} disabled={!canSubmit}>
          Reset password
        </Button>

        <p className="accept__note">
          <Icon name="lock" size={12} />
          This will sign you out everywhere else.
        </p>
      </form>
    </div>
  );
}

function Brand() {
  return (
    <div className="accept__brand">
      <span className="accept__mark">A</span>
      <span className="accept__wordmark">
        ASTRUM<strong>CONNECT</strong>
      </span>
    </div>
  );
}
